import React from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse
} from '@mui/material';

const SidebarFilters = ({ filters, setFilters }) => {
  const categories = [
    { key: '', label: 'All Products' },
    { key: 'microsoft', label: 'Microsoft (All)' },
    { key: 'windows', label: 'Windows OS' },
    { key: 'ms-office', label: 'Microsoft Office' },
    { key: 'servers', label: 'Windows Servers' },
    { key: 'sql-server', label: 'SQL Server' },
    { key: 'visual-studio', label: 'Visual Studio' },
    { key: 'adobe', label: 'Adobe Suite' },
    { key: 'antivirus', label: 'Antivirus' },
    { key: 'auto-desk', label: 'AutoDesk' },
    { key: 'coreldraw', label: 'CorelDraw' },
    { key: 'tally', label: 'Tally Prime' },
    { key: 'combo', label: 'Combo Deals' },
  ];

  const handleCategoryChange = (catKey) => {
    // When changing category, reset sub-filters
    setFilters(prev => ({ ...prev, category: catKey, volumes: [] }));
  };

  const handlePriceChange = (event, newValue) => {
    setFilters(prev => ({ ...prev, priceRange: newValue }));
  };

  const handleSubFilterToggle = (val) => {
    const currentList = filters.volumes || [];
    const newList = currentList.includes(val)
      ? currentList.filter(v => v !== val)
      : [...currentList, val];
    setFilters(prev => ({ ...prev, volumes: newList }));
  };

  const activeCategory = String(filters.category || '').toLowerCase();

  // Dynamic Contextual Sub-filters Configuration
  let subFilterConfig = null;

  if (activeCategory === 'antivirus') {
    subFilterConfig = {
      title: 'ANTIVIRUS BRANDS',
      items: ['Quick Heal', 'Kaspersky', 'Norton', 'McAfee', 'Bitdefender'],
    };
  } else if (activeCategory === 'auto-desk' || activeCategory === 'autodesk') {
    subFilterConfig = {
      title: 'AUTODESK SUITE',
      items: ['AutoCAD', 'Revit', '3ds Max', 'Maya', 'Civil 3D'],
    };
  } else if (activeCategory === 'windows' || activeCategory.startsWith('windows-')) {
    subFilterConfig = {
      title: 'WINDOWS EDITIONS',
      items: ['Windows 11', 'Windows 10', 'Windows 8', 'Windows 7'],
    };
  } else if (activeCategory === 'ms-office' || activeCategory === 'office') {
    subFilterConfig = {
      title: 'OFFICE EDITIONS',
      items: ['Office 2024', 'Office 2021', 'Office 2019', 'Home & Business'],
    };
  } else if (activeCategory === 'adobe') {
    subFilterConfig = {
      title: 'ADOBE APPS',
      items: ['Acrobat Pro', 'Photoshop', 'Illustrator', 'Premiere Pro'],
    };
  }

  return (
    <Box sx={{ pr: { md: 2 } }}>
      {/* Category List */}
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5, fontSize: '0.95rem', color: '#0F172A', letterSpacing: 0.5 }}>
        CATEGORIES
      </Typography>
      <List dense sx={{ mb: 3, p: 0 }}>
        {categories.map((cat) => {
          const isSelected = activeCategory === String(cat.key).toLowerCase();
          return (
            <ListItem key={cat.label} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => handleCategoryChange(cat.key)}
                sx={{
                  py: 0.7,
                  px: 1.5,
                  borderRadius: 1.5,
                  transition: 'all 0.2s ease',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(14, 106, 218, 0.08)',
                    color: '#0E6ADA',
                    '& .MuiTypography-root': { fontWeight: 700 }
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(14, 106, 218, 0.04)',
                    color: '#0E6ADA'
                  }
                }}
              >
                <ListItemText
                  primary={cat.label}
                  primaryTypographyProps={{
                    fontSize: '0.88rem',
                    color: isSelected ? '#0E6ADA' : '#334155'
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ mb: 3 }} />

      {/* Price Range */}
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, fontSize: '0.95rem', color: '#0F172A', letterSpacing: 0.5 }}>
        PRICE RANGE
      </Typography>
      <Box sx={{ px: 1, mb: 3 }}>
        <Slider
          value={filters.priceRange || [0, 10000]}
          onChange={handlePriceChange}
          valueLabelDisplay="auto"
          min={0}
          max={10000}
          step={100}
          sx={{
            color: '#0E6ADA',
            '& .MuiSlider-thumb': {
              boxShadow: '0 2px 6px rgba(14, 106, 218, 0.4)'
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B' }}>₹{filters.priceRange?.[0] ?? 0}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B' }}>₹{filters.priceRange?.[1] ?? 10000}</Typography>
        </Box>
      </Box>

      {/* Dynamic Contextual Sub-Filter Section */}
      <Collapse in={Boolean(subFilterConfig)}>
        {subFilterConfig && (
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5, fontSize: '0.95rem', color: '#0F172A', letterSpacing: 0.5 }}>
              {subFilterConfig.title}
            </Typography>
            <FormGroup>
              {subFilterConfig.items.map((item) => (
                <FormControlLabel
                  key={item}
                  control={
                    <Checkbox
                      size="small"
                      checked={(filters.volumes || []).includes(item)}
                      onChange={() => handleSubFilterToggle(item)}
                      sx={{
                        color: '#CBD5E1',
                        '&.Mui-checked': { color: '#0E6ADA' }
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.84rem', color: '#334155' }}>{item}</Typography>}
                  sx={{ mb: 0.3 }}
                />
              ))}
            </FormGroup>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default SidebarFilters;
